<template>
<q-page class="main">
  <q-card class="page-card">
    <BaseMap class="page-card__map"
             :geometry="project.json_polygon"
             auto-fit
             canFullscreen
             canZoom
             :canPan="false" />
    <q-card-section class="page-card__main-content">
      <item-header :item="phase" />
    </q-card-section>
    <q-card-section class="page-card__details"
                    style="scroll-behavior: smooth">
      <div class="row items-center justify-between q-gutter-sm">
        <template v-if="$can('manage-rules') || $can('test-own-projects')">
          <flat-button color="accent"
                       outline
                       :label="projectRules.showsIrrelevant ? 'Verberg niet van toepassing' : 'Toon alle richtlijnen'"
                       :icon="projectRules.showsIrrelevant ? 'visibility_off' : 'visibility'"
                       @click="projectRules.toggleIrrelevant" />
        </template>
        <template v-if="projectRules.hasOldVersions && ($can('manage-rules') || canUpdateBindings)">
          <flat-button outline label="Werk richtlijn versies bij"
                       @click="updateRuleVersions" />
        </template>
        <flat-button v-if="$can('requestApproval', project) && hasRulesToRequest"
                     label="Ter toetsing indienen" icon="done" outline
                     @click="requestApproval" />
      </div>
      <expansion-element :label="category.title" v-for="category in projectRules.categories" :key="category.id"
                         :items-count="projectRules.ruleVersions[category.id].length"
                         :id="`category-${category.id}`">
        <template #buttons>
          <flat-button v-if="canApproveRulesInCategory(category.id)"
                       outline
                       @click.stop="approveCategory(category)">
            <template v-if="isMultiRoundApproval">
              Gereed voor volgende fase
            </template>
            <template v-else>
              Keur goed
            </template>
          </flat-button>
          <flat-button
              v-else-if="$can('checkOffRules', project) && !$can('test-own-projects') && hasRulesToCheckOff[category.id]"
              outline
              @click.stop="checkOffCategory(category)">
            Markeer afgerond
          </flat-button>
        </template>
        <project-rule-list :ruleVersions="projectRules.ruleVersions[category.id]"
                           :project="project"
                           :phase-id="phase.id" />
      </expansion-element>
      <expansion-element
          label="Documenten" hior
          :items-count="project.assets.length"
          showEmpty>
        <document-list
            :can-edit="$can('addFiles', project)"
            :documents="project.assets"
            :uploadUrl="$route('project.rule.add_file', [project.id, phase.id])"
            :deleteUrl="$route('project.delete_file', project.id)" />
      </expansion-element>
    </q-card-section>
  </q-card>
  <sticky-top-bar>
    <template v-slot:actions>
      <flat-button color="accent" label="Print fase" icon="print" @click="printPhase" />
    </template>
    <template v-slot:menu_items>
      <nav-list-item
          v-if="previousPhase && $can('promote', project)"
          label="Degradeer project"
          color="accent"
          icon="undo"
          action
          @click="demoteProject"
      />
      <nav-list-item
          v-if="$can('manage-rules') && $can('manage-projects')"
          label="Beheer flexibele richtlijnen"
          icon="view_timeline"
          action
          @click="addFlexibleRule"
      />
      <nav-list-item
          v-if="$can('addCustomRules', project)"
          label="Voeg project-specifieke richtlijn toe"
          icon="playlist_add"
          action
          @click="addCustomRule"
      />
      <nav-list-item
          label="Exporteer deze fase"
          color="accent"
          icon="note_add"
          action
          @click="exportPhase"
      />
    </template>
  </sticky-top-bar>
  <sticky-bottom-bar>
    <template v-if="$isDesktop">
      <template v-if="phaseIsActive">
        <common-fab
            v-if="$can('promote', project)"
            icon="task_alt"
            label="Promoveer project"
            @click="promoteProject"
        />
        <common-fab
            v-else-if="$can('requestApproval', project) && hasRulesToRequest"
            label="Ter toetsing indienen"
            icon="done"
            @click="requestApproval" />
      </template>
    </template>
    <common-fab v-else>
      <template v-slot:actions>
        <template v-if="phaseIsActive">
          <fab-action
              v-if="$can('promote', project)"
              icon="task_alt"
              label="Promoveer project"
              @click="promoteProject"
          />
          <fab-action
              v-else-if="$can('requestApproval', project) && hasRulesToRequest"
              label="Ter toetsing indienen"
              icon="done"
              @click="requestApproval" />
        </template>
        <fab-action
            v-if="previousPhase && $can('promote', project)"
            icon="undo"
            label="Degradeer project"
            @click="demoteProject"
        />
        <fab-action
            v-if="$can('addCustomRules', project)"
            label="Beheer flexibele richtlijnen"
            icon="view_timeline"
            @click="addFlexibleRule"
        />
        <fab-action
            v-if="$can('manage-rules') && $can('manage-projects')"
            label="Voeg project-specifieke richtlijn toe"
            icon="playlist_add"
            @click="addCustomRule"
        />
        <fab-action
            icon="note_add"
            label="Exporteer deze fase"
            @click="exportPhase"
        />
      </template>
    </common-fab>
  </sticky-bottom-bar>
  <promote-phase-dialog v-if="phaseIsActive"
                        v-model="showPromotePhaseDialog"
                        :project-id="project.id"
                        :phase="phase"
                        :ruleVersions="projectRules.ruleVersions"
                        :categories="projectRules.categories" />
</q-page>
</template>

<script>
import ProjectRuleList from '~HIOR/components/lists/ProjectRuleList'
import { STATUS_ACTIVE } from '~HIOR/constants/phaseStatus'
import PromotePhaseDialog from '~HIOR/pages/project/rule/dialog/PromotePhaseDialog'
import DocumentList from '~HIOR/components/lists/DocumentList'
import { ruleMixin } from '~HIOR/mixins'
import PrimaryButton from '../../../components/button/PrimaryButton.vue'
import { useProjectRules } from '../../../stores/useProjectRules'

export default {
  setup (props) {
    const projectRules = useProjectRules()
    projectRules.load(props.project.id, props.phase.id, props.showIrrelevant)
    return {
      projectRules,
    }
  },
  components: {
    PrimaryButton,
    ProjectRuleList,
    PromotePhaseDialog,
    DocumentList,
  },
  mixins: [ruleMixin],
  props: {
    project: {
      type: Object,
    },
    phase: {
      type: Object,
    },
    phases: {
      type: Array,
    },
    showIrrelevant: {
      type: Boolean,
    },
    previousPhase: {
      type: Object,
    },
  },
  data () {
    return {
      showPromotePhaseDialog: false,
    }
  },
  computed: {
    phaseIsActive () {
      return this.phase.pivot?.status === STATUS_ACTIVE
    },
    hasRulesToCheckOff () {
      return this.projectRules.categories.reduce((groups, category) => {
        groups[category.id] = this.projectRules.ruleVersions[category.id].some(({ pivot }) => {
          return !this.$ruleIsApproved(pivot.approver_status)
              && !this.$ruleIsIrrelevant(pivot.approver_status)
              && this.$ruleIsIncomplete(pivot.implementer_status)
        })
        return groups
      }, [])
    },
    hasUncheckedRules () {
      return this.projectRules.categories.some(category => {
        return this.projectRules.ruleVersions[category.id].some(({ pivot }) => {
          return !this.$ruleIsApproved(pivot.implementer_status)
              && (this.$ruleIsIncomplete(pivot.approver_status) || this.$ruleIsRejected(pivot.approver_status))
        })
      })
    },
    hasRulesToRequest () {
      return this.projectRules.categories.some(category => {
        return this.projectRules.ruleVersions[category.id].some(({ pivot }) => {
          return !this.$ruleIsAwaitingApproval(pivot.approver_status) &&
              !this.$ruleIsApproved(pivot.approver_status)
        })
      })
    },
    canUpdateBindings () {
      return this.projectRules.categories.some(category => {
        return this.projectRules.ruleVersions[category.id].some(({ rule_id }) => {
          return this.$can('updateBindingForProject', rule_id)
        })
      })
    },
    isMultiRoundApproval () {
      return Object.values(this.projectRules.ruleVersions)
          .some(ruleGroup => {
            return ruleGroup.some(ruleVersion => {
              return !this.$ruleIsIncomplete(ruleVersion.pivot.implementer_status)
            })
          })
    },
  },
  methods: {
    exportPhase () {
      this.$openWindow('project.rule.print', { project_id: this.project.id, phase_id: this.phase.id, type: 'excel' })
    },
    printPhase () {
      this.$openWindow('project.rule.print', { project_id: this.project.id, phase_id: this.phase.id, type: 'pdf' })
    },
    addFlexibleRule () {
      this.$goto('flexible-rule.index', { project_id: this.project.id })
    },
    addCustomRule () {
      this.$goto('custom-rule.create', { project_id: this.project.id })
    },
    async requestApproval () {
      const messages = [
        'Weet u zeker dat u deze fase ter toetsing wil aanbieden? Controleer of u het project van de  juiste informatie heeft voorzien om deze fase te laten toetsen.',
      ]
      if (this.hasUncheckedRules) {
        messages.unshift('Let op! U heeft niet alle richtlijnen afgevinkt of gemarkeerd als voldaan.')
      }
      if (!await this.$confirm({ title: 'Ter toetsing aanbieden', messages })) return
      try {
        await axios.post(this.$route('project.rule.request_approval', [this.project.id, this.phase.id]))
        this.$toast('success', 'De aanvraag is ingediend')
        this.projectRules.reload()
      } catch (e) {
        console.error(e)
        this.$toast('error', 'De aanvraag kon niet worden uitgevoerd')
      }
    },
    promoteProject () {
      this.showPromotePhaseDialog = true
    },
    canApproveRulesInCategory (categoryId) {
      return this.projectRules.ruleVersions[categoryId].some(ruleVersion => {
        return !this.$ruleIsApproved(ruleVersion.pivot.approver_status) &&
            !this.$ruleIsIrrelevant(ruleVersion.pivot.approver_status) &&
            !this.$ruleIsIrrelevant(ruleVersion.pivot.implementer_status)
            && this.$can('approve', ruleVersion.rule)
      })
    },
    async updateRuleVersions () {
      if (!await this.$confirm(
          {
            title: 'Richtlijnen in deze fase bijwerken',
            messages: [`"Weet u zeker dat u alle richtlijnversies binnen de fase ${this.phase.title} voor dit project wilt bijwerken naar de meest recente versie?"`],
          },
      )) {
        return
      }
      try {
        const response = await axios.post(
            route('project.update_bindings', { project: this.project.id, phase: this.phase.id }),
        )
        this.projectRules.reload()
        this.$toast('success', response.data.message)
      } catch (e) {
        console.error(e)
        this.$toast('error', 'De aanvraag kon niet worden uitgevoerd')
      }
    },
    async checkOffCategory (category) {
      try {
        await axios.post(this.$route('project.rule.checkoff_category', [this.project.id, this.phase.id, category.id]))
        this.projectRules.reload()
      } catch (e) {
        console.error(e)
        this.$toast('error', 'De aanvraag kon niet worden uitgevoerd')
      }
    },
    async approveCategory (category) {
      let title = 'Keur goed'
      let messages = [`Weet u zeker dat u alle relevante richtlijnen in de categorie ${category.title} wilt goedkeuren?`]
      if (this.isMultiRoundApproval) {
        title = 'Gereed voor volgende fase'
        messages = [`Weet u zeker dat u alle richtlijnen binnen  de categorie ${category.title} gereed zijn voor de volgende fase?`, 'Dit betekent dat richtlijnen (of overeengekomen afwijkingen hiervan) akkoord zijn of afspraken zijn gemaakt voor invulling in de volgende fase.']
      }

      if (!await this.$confirm({ title, messages })) {
        return
      }
      try {
        const response = await axios.post(this.$route('project.rule.approve_category', [this.project.id, this.phase.id, category.id]))
        this.projectRules.reload()
        this.$toast('success', response.data.message)
      } catch (e) {
        console.error(e)
        this.$toast('error', 'De aanvraag kon niet worden uitgevoerd')
      }
    },
    async demoteProject () {
      if (!await this.$confirm(
          {
            title: 'Degradeer project',
            messages: [`Weet u zeker dat u dit project wilt degraderen naar de fase ${this.previousPhase.title}?`],
          },
      )) {
        return
      }

      this.$goto('project.demote', { project_id: this.project.id })
    },
    fixCategoryScroll () {
      if (!location.hash) {
        return
      }
      try {
        const targetElement = this.$el.querySelector(location.hash)
        const toolbar = document.querySelector('.page-toolbar')
        const scrollContainer = document.querySelector('.scroll')
        if (!targetElement || !toolbar || !scrollContainer) {
          return
        }

        const { height: toolbarHeight, top: toolbarTop } = toolbar.getBoundingClientRect()
        const toolbarOffset = toolbarHeight + toolbarTop
        const { top: currentTop } = targetElement.getBoundingClientRect()
        scrollContainer.scrollTo(0, scrollContainer.scrollTop + (currentTop - toolbarOffset))
        location.hash = ''
      } catch {
        //ignore
      }
    },
  },
  mounted () {
    this.fixCategoryScroll()
    window.addEventListener('hashchange', this.fixCategoryScroll.bind(this))
  },
  beforeUnmount () {
    window.removeEventListener('hashchange', this.fixCategoryScroll.bind(this))
  },
}
</script>
